import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  on,
  untrack,
  type Accessor,
} from 'solid-js';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import type { ReferenceType } from '@data-access/geospatial/fx-workspace-service';

import { useAppContext } from '../AppContextProvider';
import { SidePanelTabName } from '../constants/panel-constants';
import type { SyncFxWorkspaceReferencesInput } from './fx-workspace-repository';
import { useFxWorkspaceDraftReferences } from './use-fx-workspace-draft-references';

export interface FxWorkspaceDataEditingApi {
  readonly isWorkspaceDataEditing: Accessor<boolean>;
  readonly editingWorkspaceId: Accessor<string | undefined>;
  readonly isEditingWorkspaceReferencesLoaded: Accessor<boolean>;
  readonly draftReferences: Accessor<ReadonlySet<string>>;
  enterWorkspaceDataEditing(): void;
  toggleDraftReference(refTrn: string, type: ReferenceType): void;
  isDraftReference(refTrn: string): boolean;
  setDraftReferenceSelection(
    refTrns: readonly string[],
    selected: boolean,
    type: ReferenceType
  ): void;
  commitWorkspaceDataEditing(): Promise<boolean>;
  cancelWorkspaceDataEditing(): void;
  resetSession(): void;
}

export function useFxWorkspaceDataEditing(options: {
  activeWorkspaceId: Accessor<string | undefined>;
  getWorkspaceReferences(workspaceId: string): ReadonlySet<string>;
  areWorkspaceReferencesLoaded(workspaceId: string): boolean;
  syncWorkspaceReferences(
    workspaceId: string,
    input: SyncFxWorkspaceReferencesInput
  ): Promise<boolean>;
}): FxWorkspaceDataEditingApi {
  const appContext = useAppContext();
  const [isWorkspaceDataEditing, setIsWorkspaceDataEditing] =
    createSignal(false);
  const [editingWorkspaceId, setEditingWorkspaceId] = createSignal<
    string | undefined
  >(undefined);
  const draft = useFxWorkspaceDraftReferences({
    isEditing: isWorkspaceDataEditing,
  });
  const [isDraftBaselineMerged, setIsDraftBaselineMerged] = createSignal(false);

  const isEditingWorkspaceReferencesLoaded = createMemo((): boolean => {
    const workspaceId = editingWorkspaceId();
    return (
      workspaceId !== undefined &&
      options.areWorkspaceReferencesLoaded(workspaceId)
    );
  });

  const resetSession = (): void => {
    batch(() => {
      setIsWorkspaceDataEditing(false);
      setEditingWorkspaceId(undefined);
      draft.reset();
      setIsDraftBaselineMerged(false);
    });
  };

  const exitEditingAndReturnToWorkspaces = (): void => {
    resetSession();
    appContext.setSelectedSidePanelTab(SidePanelTabName.Workspaces);
  };

  const mergeLoadedReferencesBaseline = (workspaceId: string): void => {
    if (
      !options.areWorkspaceReferencesLoaded(workspaceId) ||
      isDraftBaselineMerged()
    ) {
      return;
    }

    batch(() => {
      draft.mergeLoaded(options.getWorkspaceReferences(workspaceId));
      setIsDraftBaselineMerged(true);
    });
  };

  createEffect(
    on(
      () => options.activeWorkspaceId(),
      (activeId) => {
        if (!untrack(isWorkspaceDataEditing)) {
          return;
        }

        if (untrack(editingWorkspaceId) !== activeId) {
          exitEditingAndReturnToWorkspaces();
        }
      }
    )
  );

  createEffect(
    on(
      () => {
        const workspaceId = editingWorkspaceId();
        return {
          isEditing: isWorkspaceDataEditing(),
          workspaceId,
          areReferencesLoaded:
            workspaceId !== undefined &&
            options.areWorkspaceReferencesLoaded(workspaceId),
        };
      },
      ({ isEditing, workspaceId, areReferencesLoaded }) => {
        if (!isEditing || workspaceId === undefined || !areReferencesLoaded) {
          return;
        }

        mergeLoadedReferencesBaseline(workspaceId);
      }
    )
  );

  const enterWorkspaceDataEditing = (): void => {
    const workspaceId = options.activeWorkspaceId();
    if (workspaceId === undefined) {
      return;
    }

    batch(() => {
      if (!isWorkspaceDataEditing() || editingWorkspaceId() !== workspaceId) {
        setIsDraftBaselineMerged(false);
        if (options.areWorkspaceReferencesLoaded(workspaceId)) {
          draft.replace(options.getWorkspaceReferences(workspaceId));
          setIsDraftBaselineMerged(true);
        } else {
          draft.reset();
        }
        setEditingWorkspaceId(workspaceId);
        setIsWorkspaceDataEditing(true);
      }

      appContext.setSelectedSidePanelTab(SidePanelTabName.ProjectData);
    });
  };

  const commitWorkspaceDataEditing = async (): Promise<boolean> => {
    const workspaceId = editingWorkspaceId();
    if (!isWorkspaceDataEditing() || workspaceId === undefined) {
      return false;
    }

    if (!options.areWorkspaceReferencesLoaded(workspaceId)) {
      NotificationEventBus.emit({
        type: 'error',
        message: t('mobileMapping.fxWorkspace.workspaceDataEditing.notReady'),
      });
      return false;
    }

    mergeLoadedReferencesBaseline(workspaceId);

    const saved = await options.syncWorkspaceReferences(
      workspaceId,
      draft.diff(options.getWorkspaceReferences(workspaceId))
    );
    if (saved) {
      exitEditingAndReturnToWorkspaces();
    }

    return saved;
  };

  const cancelWorkspaceDataEditing = (): void => {
    if (!isWorkspaceDataEditing()) {
      return;
    }

    exitEditingAndReturnToWorkspaces();
  };

  return {
    isWorkspaceDataEditing,
    editingWorkspaceId,
    isEditingWorkspaceReferencesLoaded,
    draftReferences: draft.draftReferences,
    enterWorkspaceDataEditing,
    toggleDraftReference: draft.toggleDraftReference,
    isDraftReference: draft.isDraftReference,
    setDraftReferenceSelection: draft.setDraftReferenceSelection,
    commitWorkspaceDataEditing,
    cancelWorkspaceDataEditing,
    resetSession,
  };
}
